import { Spinner } from "@ui-kit/feedback/Spinner/Spinner.web";
import Box, { type BoxProps } from "../../layout/Box/Box.web";
import type { TileSharedProps } from "./Tile.props";

function Tile({
  variant = "complex",
  ...props
}: TileProps & {
  variant?: "simple" | "complex";
}): JSX.Element {
  if (variant === "simple") {
    return <TileSimple {...props} />;
  }

  return <TileComplex {...props} />;
}

Tile.Simple = TileSimple;
function TileSimple({
  children,
  leadingElement,
  trailingElement,
  onClick,
  alignment = "left",
  direction = "column",
  isLoading = false,
  outlined = false,
  ...props
}: TileProps & {
  direction?: "row" | "column";
  alignment?: "left" | "center";
  outlined?: boolean;
}): JSX.Element {
  let alignItems: BoxProps["alignItems"] = "flex-start";
  let justifyContent: BoxProps["justifyContent"] = "flex-start";
  let textAlign: BoxProps["textAlign"] = "left";

  if (direction === "row") {
    // row
    alignItems = alignment === "left" ? "flex-start" : "center";
    justifyContent = "space-between";
    textAlign = "left";
  } else {
    // column
    alignItems = alignment === "left" ? "flex-start" : "center";
    justifyContent = alignment === "left" ? "flex-start" : "center";
    textAlign = alignment === "left" ? "left" : "center";
  }

  return (
    <Box
      {...props}
      //
      as="button"
      type="button"
      tabIndex={0}
      role="button"
      //
      display="flex"
      flexDirection={direction}
      alignItems={alignItems}
      justifyContent={justifyContent}
      textAlign={textAlign}
      gap="sm"
      //
      cursor="pointer"
      padding="md"
      borderRadius="md"
      transition="background 200ms cubic-bezier(0, 0, 1, 1), border-color 200ms cubic-bezier(0, 0, 1, 1), outline-color 200ms cubic-bezier(0, 0, 1, 1);"
      css={{
        ...(outlined
          ? {
              // Use outline + border to achieve a thicker "border" style on hover without layout shifts
              outlineWidth: "1px !important",
              outline: "solid",
              outlineColor: "transparent",
              "&:hover, &:focus": {
                borderColor: "primary.900",
                outlineWidth: "1px !important",
                outline: "solid",
                outlineColor: "primary.900",
              },
              "&:active": {
                backgroundColor: "gray.200",
                outlineWidth: "1px !important",
                outline: "solid",
                outlineColor: "primary.800",
              },
            }
          : {
              "&:hover, &:focus": {
                backgroundColor: "primary.900",
                color: "white",
              },
              "&:active": {
                backgroundColor: "primary.600",
              },
            }),
      }}
      //
      border={outlined ? "1px solid" : "none"}
      borderColor={outlined ? "gray.400" : "transparent"}
      //
      onClick={onClick}
    >
      {isLoading ? (
        <Box>
          <Spinner />
        </Box>
      ) : (
        <>
          {leadingElement && <Box>{leadingElement}</Box>}
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            {children}
          </Box>
          {trailingElement && <Box>{trailingElement}</Box>}
        </>
      )}
    </Box>
  );
}

Tile.Complex = TileComplex;
function TileComplex({
  children,
  leadingElement,
  trailingElement,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isSelected = false,
  isLoading = false,
  disabled = false,
  ...props
}: TileProps & {
  isSelected?: boolean;
}): JSX.Element {
  return (
    <Box
      {...props}
      //
      as="button"
      type="button"
      tabIndex={0}
      disabled={disabled}
      role="button"
      //
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      gap="sm"
      //
      cursor="pointer"
      padding="md"
      borderRadius="md"
      borderWidth="1px !important"
      border="solid"
      borderColor="gray.400"
      transition="background 200ms cubic-bezier(0, 0, 1, 1), border-color 200ms cubic-bezier(0, 0, 1, 1), outline-color 200ms cubic-bezier(0, 0, 1, 1);"
      css={{
        // Use outline + border to achieve a thicker "border" style on hover without layout shifts
        outlineWidth: "1px !important",
        outline: "solid",
        outlineColor: "transparent",
        ...(!disabled && {
          "&:hover": {
            borderColor: "gray.600",
            outlineWidth: "1px !important",
            outline: "solid",
            outlineColor: "gray.600",
          },
        }),
        "&:focus": {
          borderColor: "primary.900",
          outlineWidth: "0px !important",
          outline: "solid !important",
          outlineColor: "primary.900 !important",
        },
        "&:active": {
          backgroundColor: "gray.200",
          outlineWidth: "1px !important",
          outline: "1px solid !important",
          outlineColor: "primary.800",
        },
        ...(isSelected && {
          borderColor: "primary.800",
          outlineWidth: "1px !important",
          outline: "1px solid !important",
          outlineColor: "primary.800",
        }),
      }}
      //
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap="sm"
        width="100%"
      >
        {leadingElement && (
          <Box
            width="element.md"
            height="element.md"
            display="flex"
            alignItems="center"
          >
            {isLoading ? <Spinner size="sm" /> : leadingElement}
          </Box>
        )}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
        >
          {children}
        </Box>
      </Box>

      {trailingElement && <Box>{trailingElement}</Box>}
    </Box>
  );
}

export default Tile;

export type TileProps = TileSharedProps & {
  // Add any web specific props here.
};
